/**
 * Web push on this device: ask for permission, subscribe through the
 * service worker and register the subscription with the server.
 * The service worker shows the notifications (public/push-sw.js).
 */
import i18n from "../i18n";
import {
  getPushPublicKey,
  subscribePush,
  unsubscribePush,
} from "../api/notifications/push";

export const isPushSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// iPhones/iPads only support push when the app is added to the home screen
export const needsHomeScreenInstall = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.matchMedia("(display-mode: standalone)").matches &&
  !navigator.standalone;

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
};

const getRegistration = async () => {
  if (!isPushSupported()) return null;
  return (await navigator.serviceWorker.getRegistration()) || null;
};

export const getPushSubscription = async () => {
  const registration = await getRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
};

export const enablePush = async () => {
  const registration = await getRegistration();
  if (!registration) {
    throw new Error(i18n.t("notifications.push.notAvailable"));
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(i18n.t("notifications.push.blocked"));
  }

  const { enabled, publicKey } = await getPushPublicKey();
  if (!enabled) {
    throw new Error(i18n.t("notifications.push.notSetUp"));
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  await subscribePush(subscription.toJSON());
};

export const disablePush = async () => {
  const subscription = await getPushSubscription();
  if (!subscription) return;
  await unsubscribePush(subscription.endpoint).catch(() => {});
  await subscription.unsubscribe();
};

// On logout: stop this device's push without calling the server (the
// server removes the subscription the next time a push to it fails)
export const unsubscribeDeviceOnly = async () => {
  const subscription = await getPushSubscription().catch(() => null);
  await subscription?.unsubscribe().catch(() => {});
};
